import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "component/auth/OnboardingLayout";
import ConversionConfirmModal from "component/wallet/ConversionConfirmModal";
import ConversionSuccessModal from "component/wallet/ConversionSuccessModal";
import CurrencyToggle from "component/currencyToggle";
import { ArrowDown2, PercentageCircle, ReceiptDiscount } from "iconsax-reactjs";
import { CurrencySelector } from "component/wallet/CurrencySelector";
import { formatMoney } from "utilities/formatMoney";
import currencyPairCheck from "utilities/currencyPairCheck";
import { fetchFxRate } from "api/fetchers";
import { fetchTransferFees } from "api/fetchers";
import { normalizeFees } from "api/fees";
import axios from "axios";
import PinModal from "../../component/pay/PinModal";
import { generateSesid } from "../../utilities/generateSesid";
import TransferSuccessModal from "../../component/pay/TransferSuccessModal";
import { InputNumberFormat, format, unformat } from "@react-input/number-format";

const DEBOUNCE_MS = 1000;

const CurrencyConverter = () => {
    const navigate = useNavigate();
    const sesidRef = useRef(null);
    // redux
    const { business, currency: defaults } = useSelector((s) => s);
    const wallets = useSelector((s) => s.wallets || []);
    const businessId = business?._id;
    const isBusinessApproved = business?.status === "APPROVED";
    const fromCurrency = String(defaults || "").toUpperCase();

    const [showPin, setShowPin] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [rateQuote, setRateQuote] = useState({ key: "", fingerprint: "" });

    // local state
    const [amount, setAmount] = useState("");
    const numericAmount = Number.parseFloat(amount || "0") || 0;

    const [toCurrency, setToCurrency] = useState("");
    const [toMeta, setToMeta] = useState(null);
    const isToUSD = String(toCurrency || "").toUpperCase() === "USD";
    const paymentRail = isToUSD ? "ach" : "";
    const currentRateFingerprint = [
        fromCurrency,
        String(toCurrency || "").toUpperCase(),
        String(Number.isFinite(numericAmount) ? numericAmount : 0),
    ].join("|");
    const rateKey =
        rateQuote.fingerprint === currentRateFingerprint ? String(rateQuote.key || "") : "";

    const [rate, setRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    const [rateError, setRateError] = useState("");

    // fee state
    const [feeLoading, setFeeLoading] = useState(false);
    const [feePercent, setFeePercent] = useState(0);
    const [feeFlat, setFeeFlat] = useState(0);

    const [apiTxnFee, setApiTxnFee] = useState(null);
    const [apiFeeIsPercent, setApiFeeIsPercent] = useState(false);
    const [apiMin, setApiMin] = useState(null);
    const [apiMax, setApiMax] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const sameCurrency = toCurrency && fromCurrency === String(toCurrency).toUpperCase();

    const LOCALE = "en";

    const formattedAmount = useMemo(() => {
        return amount ? format(amount, { locales: LOCALE, maximumFractionDigits: 2 }) : "";
    }, [amount]);

    const balance = useMemo(() => {
        const w = wallets.find((w) => String(w.currency || "").toUpperCase() === fromCurrency);
        return Number(w?.balance || 0);
    }, [wallets, fromCurrency]);

    const isCryptoCode = (code) => {
        const u = String(code || "").toUpperCase();
        if (u === "USDC" || u === "USDT") return false;
        try {
            const info = currencyPairCheck(u, { returnType: "full" });
            return info?.isCrypto === true;
        } catch {
            return ["BTC", "ETH"].includes(u);
        }
    };

    const fiatWallets = useMemo(
        () => (wallets || []).filter((w) => !isCryptoCode(w?.currency)),
        [wallets]
    );

    useEffect(() => {
        setToCurrency("");
        setToMeta(null);
    }, [fromCurrency]);

    useEffect(() => {
        if (!sesidRef.current) {
            sesidRef.current = generateSesid(business?._id);
        }
    }, [business?._id]);

    useEffect(() => {
        if (!businessId || !toCurrency || sameCurrency || numericAmount <= 0 || !rateKey) {
            setFeeLoading(false);
            setApiTxnFee(null);
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        const timer = setTimeout(async () => {
            try {
                setFeeLoading(true);

                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: String(fromCurrency).toUpperCase(),
                        currencyTo: String(toCurrency).toUpperCase(),
                        amount: Number.isFinite(numericAmount) ? numericAmount : 0,
                        rateKey,
                        ...(paymentRail ? { paymentRail } : {}),
                    },
                    signal: controller.signal,
                });

                const nf = normalizeFees(res) || {};
                const pct = Number(nf.fxTransferFee) || 0;
                const flat = Number(nf.flatFee || nf.baseFee || 0);

                const raw = (res?.data ?? res) || {};
                const bag = raw.data || raw;

                const txnFee = Number(bag.transactionFee);
                const isPercentFlag = Boolean(!bag.isFlat);
                const minAmt = Number.isFinite(Number(bag.minimumTransferAmount))
                    ? Number(bag.minimumTransferAmount)
                    : null;
                const maxAmt = Number.isFinite(Number(bag.maximumTransferAmount))
                    ? Number(bag.maximumTransferAmount)
                    : null;

                if (!cancelled) {
                    setFeePercent(pct);
                    setFeeFlat(flat);
                    setApiTxnFee(Number.isFinite(txnFee) ? txnFee : null);
                    setApiFeeIsPercent(isPercentFlag);
                    setApiMin(minAmt);
                    setApiMax(maxAmt);
                }
            } catch (e) {
                if (!cancelled && e?.name !== "AbortError") {
                    setRateError(e?.message || "Failed to fetch fee");
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            controller.abort();
        };
    }, [
        businessId,
        fromCurrency,
        toCurrency,
        numericAmount,
        sameCurrency,
        rateKey,
        paymentRail,
    ]);

    useEffect(() => {
        const to = String(toCurrency || "").toUpperCase();
        if (!businessId || !fromCurrency || !to || sameCurrency || numericAmount <= 0) {
            setRate(null);
            setRateLoading(false);
            setRateError("");
            setRateQuote({ key: "", fingerprint: "" });
            return;
        }

        let cancelled = false;
        (async () => {
            setRateLoading(true);
            setRateError("");
            try {
                const fx = await fetchFxRate({
                    fromCurrency,
                    toCurrency: to,
                    amount: Number.isFinite(numericAmount) ? numericAmount : 0,
                    businessId,
                    ...(paymentRail ? { paymentRail } : {}),
                });
                const r = Number(fx?.roundedRate || fx?.rate || 0);
                if (!cancelled) {
                    setRate(Number.isFinite(r) && r > 0 ? r : null);
                    setRateQuote({
                        key: String(fx?.rateKey ?? fx?.key ?? "") || "",
                        fingerprint: currentRateFingerprint,
                    });
                }
            } catch (e) {
                if (!cancelled) {
                    setRate(null);
                    setRateError(e?.message || "Failed to fetch FX rate");
                    setRateQuote({ key: "", fingerprint: "" });
                }
            } finally {
                if (!cancelled) setRateLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        businessId,
        fromCurrency,
        toCurrency,
        numericAmount,
        sameCurrency,
        currentRateFingerprint,
        paymentRail,
    ]);

    async function submitConversion({ pin }) {
        const sesid = generateSesid(businessId);
        const payload = {
            currencyFrom: String(fromCurrency).toUpperCase(),
            currencyTo: String(toCurrency).toUpperCase(),
            amount: String(numericAmount),
            rateKey: rateKey || undefined,
            isConversion: true,
            sesid,
            pin,
        };

        const headers = businessId ? { "x-business-id": businessId } : undefined;

        const res = await axios.post("/v1/wallets/cross-currency/transfer", payload, { headers });
        return res?.data;
    }

    const feeValue = apiTxnFee;

    const netSend = Math.max(0, numericAmount);
    const convertedAmount = useMemo(() => {
        if (!rate || !netSend) return 0;
        return netSend * rate;
    }, [netSend, rate]);
    const formattedConvertedAmount = useMemo(() => {
        const value = Number(convertedAmount);
        if (!Number.isFinite(value) || value === 0) return "0";
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }, [convertedAmount]);

    const insufficientFunds = numericAmount > balance || netSend > balance;
    const tooLow =
        Number.isFinite(apiMin) && apiMin !== null && numericAmount > 0 && numericAmount < apiMin;
    const tooHigh =
        Number.isFinite(apiMax) && apiMax !== null && numericAmount > 0 && numericAmount > apiMax;
    const canSubmit =
        !sameCurrency &&
        !rateLoading &&
        !feeLoading &&
        !rateError &&
        numericAmount > 0 &&
        !tooLow &&
        !tooHigh &&
        !insufficientFunds &&
        Boolean(toCurrency) &&
        Boolean(rateKey) &&
        apiTxnFee !== null;

    return (
        <OnboardingLayout title="Convert money" showSkip={false}>
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
                <div>
                    <h2 className="text-xl mb-6 self-start">Enter amount</h2>

                    <div className="bg-white rounded-xl p-6 max-w-3xl w-full">
                        {/* FROM */}
                        <div className="border-b">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">From</span>
                                <div className="relative min-w-[100px] flex justify-end mt-3">
                                    <CurrencyToggle
                                        className="w-full text-sm bg-transparent"
                                        dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
                                        fetchWallet
                                        businessId={businessId}
                                        isDisabled={!isBusinessApproved}
                                        hideLabel
                                        excludeProviderStatuses={["pending"]}
                                    />
                                </div>
                            </div>

                            <div
                                className={`flex items-start text-7xl font-normal leading-none ${
                                    insufficientFunds ? "text-red-600" : ""
                                }`}
                            >
                                <InputNumberFormat
                                    value={formattedAmount}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const raw = unformat(v || "", LOCALE);
                                        setAmount(raw);
                                    }}
                                    locales={LOCALE}
                                    maximumFractionDigits={2}
                                    type="text"
                                    inputMode="decimal"
                                    className={`!shadow-none outline-none font-thin w-full caret-[#6F00FF] ${
                                        insufficientFunds ? "border-b border-red-500" : ""
                                    }`}
                                    placeholder="0"
                                />
                            </div>

                            <div className="flex justify-between items-start">
                                {insufficientFunds ? (
                                    <div className="text-red-600 text-sm" role="alert">
                                        Insufficient balance. Available: {formatMoney(balance)}{" "}
                                        {fromCurrency}
                                    </div>
                                ) : tooLow ? (
                                    <div className="text-red-600 text-sm" role="alert">
                                        Minimum transfer amount is {formatMoney(apiMin || 0)}{" "}
                                        {fromCurrency}
                                    </div>
                                ) : tooHigh ? (
                                    <div className="text-red-600 text-sm" role="alert">
                                        Maximum transfer amount is {formatMoney(apiMax || 0)}{" "}
                                        {fromCurrency}
                                    </div>
                                ) : (
                                    <span />
                                )}

                                <div className="text-[#6B7280] text-sm">
                                    Balance: {formatMoney(balance)} {fromCurrency}
                                </div>
                            </div>
                        </div>

                        {/* Fee + Rate */}
                        <div className="relative my-6">
                            <div className="absolute top-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                            <div className="absolute left-4 top-[0px] bottom-[0px] w-px bg-[#E1E3F4] z-0" />
                            <span className="absolute left-[9px] top-[111px]">
                                <ArrowDown2 size={16} color="#E1E3F4" />
                            </span>

                            <div className="py-4 space-y-4">
                                {/* Rate */}
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <PercentageCircle
                                            size={16}
                                            color="#6F739B"
                                            variant="Bulk"
                                        />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                        <span>Rate</span>
                                        <span>
                                            {!toCurrency ? (
                                                "Select a currency"
                                            ) : numericAmount <= 0 ? (
                                                "Enter an amount"
                                            ) : rateLoading ? (
                                                "Fetching rate…"
                                            ) : rateError ? (
                                                <span className="text-red-600" role="alert">
                                                    {rateError}
                                                </span>
                                            ) : !rate ? (
                                                "Rate unavailable"
                                            ) : (
                                                `1 ${fromCurrency} = ${rate} ${String(
                                                    toCurrency
                                                ).toUpperCase()}`
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {!isToUSD && (
                                    <div className="flex items-start gap-3 relative z-10">
                                        <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                            <ReceiptDiscount
                                                size={16}
                                                color="#6F739B"
                                                variant="Bulk"
                                            />
                                        </div>
                                        <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                            <span>Fee</span>
                                            <span>
                                                {feeLoading
                                                    ? "Fetching fee…"
                                                    : `${formatMoney(feeValue)} ${fromCurrency}`}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {isToUSD && (
                                    <div className="flex items-start gap-3 relative z-10">
                                        <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                            <ReceiptDiscount
                                                size={16}
                                                color="#6F739B"
                                                variant="Bulk"
                                            />
                                        </div>
                                        <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                            <span>Transfer Fee</span>
                                            <span>
                                                {!rateKey
                                                    ? "Waiting for rate…"
                                                    : feeLoading
                                                      ? "Fetching fee…"
                                                      : `${formatMoney(feeValue)} ${fromCurrency}`}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TO */}
                        <div className="pt-4 border-t">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-[14px] text-gray-500">To</span>
                                <CurrencySelector
                                    wallets={fiatWallets}
                                    selected={toCurrency}
                                    onSelect={(code, meta) => {
                                        setToCurrency(code);
                                        setToMeta(meta);
                                    }}
                                    exclude={[fromCurrency]}
                                    excludeProviderStatuses={["pending"]}
                                    currencyFrom={fromCurrency}
                                />
                            </div>

                            <div className="flex items-start text-7xl font-thin leading-none">
                                <span className="text-[#475467] text-2xl pt-2 mr-1">
                                    {toMeta?.symbol || ""}
                                </span>
                                <span>{formattedConvertedAmount}</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-6 flex justify-between items-center max-w-3xl w-full">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                            Instantly ⚡
                        </span>
                        <div className="flex gap-4">
                            <button
                                className="text-gray-600 font-medium"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                className={`h-12 px-6 flex items-center justify-center rounded-lg font-medium ${
                                    canSubmit
                                        ? "bg-[#6F00FF] text-white hover:opacity-95"
                                        : "bg-[#E9D7FE] text-white cursor-not-allowed"
                                }`}
                                disabled={!canSubmit}
                                onClick={() => setIsModalOpen(true)}
                                title={
                                    sameCurrency
                                        ? "Select a different currency"
                                        : rateLoading
                                          ? "Waiting for rate…"
                                          : feeLoading
                                            ? "Waiting for fee…"
                                            : insufficientFunds
                                              ? "Insufficient balance"
                                              : !toCurrency
                                                ? "Select a currency"
                                                : ""
                                }
                            >
                                Convert
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm & Success */}
            <ConversionConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={() => {
                    setIsModalOpen(false);
                    setShowPin(true);
                }}
                amount={formatMoney(numericAmount)}
                convertedAmount={formatMoney(convertedAmount)}
                fee={formatMoney(feeValue)}
                rate={rate || 0}
                fromCurrency={fromCurrency}
                toCurrency={toCurrency}
            />

            <PinModal
                isOpen={showPin}
                onClose={() => setShowPin(false)}
                onConfirm={async (pin) => {
                    try {
                        setSubmitting(true);
                        await submitConversion({ pin });
                        setShowPin(false);
                        setShowSuccess(true);
                    } catch (err) {
                        throw err;
                    } finally {
                        setSubmitting(false);
                    }
                }}
                header="Enter payment PIN"
            />
            <TransferSuccessModal
                isOpen={showSuccess}
                onClose={() => {
                    navigate("/wallets");
                }}
                onDone={() => navigate("/wallets")}
            />
        </OnboardingLayout>
    );
};

export default CurrencyConverter;
